// PRD.303 — Profile / Account password reset.
//
// Pure orchestration for the Security card's "Send Password Reset Email"
// action, extracted from the page so the concurrency guard, the missing-email
// guard, and the message handling are unit testable under the repo's
// node:test harness (no DOM, no live Firebase).
//
// Deliberately takes NO target email: the address is resolved inside
// `sendPasswordResetForCurrentUser()` from the authenticated Firebase user, so
// this screen structurally cannot request a reset for another account.

/** Mutable in-flight gate, shared across clicks to block concurrent requests. */
export type PasswordResetGate = { inFlight: boolean };

export type PasswordResetDeps = {
  gate: PasswordResetGate;
  /** The authenticated Firebase user's email, or null/empty when there is none. */
  getEmail: () => string | null | undefined;
  /** Sends the reset email to the authenticated user. Takes no target by design. */
  send: () => Promise<void>;
};

export type PasswordResetResult =
  | { status: 'sent'; message: string }
  | { status: 'busy' }
  | { status: 'error'; message: string };

export const NO_EMAIL_MESSAGE = 'No email is associated with this account.';

/** Generic fallback for anything that is not already a user-safe Error. */
export const GENERIC_ERROR_MESSAGE = 'Could not send the password reset email. Please try again.';

export function passwordResetSuccessMessage(email: string): string {
  return `Password reset email sent to ${email}. Check your inbox and spam folder.`;
}

/**
 * Request a password reset email for the authenticated user.
 *
 * Returns `busy` (without sending) when a previous request is still in flight,
 * so repeated clicks cannot produce concurrent Firebase calls.
 */
export async function requestPasswordReset(deps: PasswordResetDeps): Promise<PasswordResetResult> {
  if (deps.gate.inFlight) return { status: 'busy' };

  // FR-6: never attempt the Firebase call without a usable account email.
  const email = deps.getEmail();
  if (!email) return { status: 'error', message: NO_EMAIL_MESSAGE };

  deps.gate.inFlight = true;
  try {
    await deps.send();
    return { status: 'sent', message: passwordResetSuccessMessage(email) };
  } catch (error: unknown) {
    // Only an Error's own message is trusted — `sendPasswordResetForCurrentUser`
    // guarantees those are already mapped to user-safe copy. Anything else
    // (a raw FirebaseError-shaped object, a string) falls back to the generic
    // message so codes and stacks never reach the UI.
    const message = error instanceof Error && error.message ? error.message : GENERIC_ERROR_MESSAGE;
    return { status: 'error', message };
  } finally {
    deps.gate.inFlight = false;
  }
}
